import { useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import AlwaysOnRoomOptionMobile from './AlwaysOnRoomOptionMobile'
import LayoutContainer from 'components/Common/LayoutContainer'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getDurationPackageOptions,
  getLowestAvailableStayPayTier,
} from 'selectors/offerPage/offerPageSelectors'
import Caption from 'LuxKit/Typography/Caption'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { buildRoomPackageOptionsViewOrderLPC, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import type { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import { initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'
import ContactContext from 'contexts/contactContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'

import Heading from 'LuxKit/Typography/Heading'
import Pane from 'components/Common/Pane'
import HotelFlightsToggleButton from 'components/OfferPage/Accommodation/HotelFlightsToggle/HotelFlightsToggleButton'

interface Props {
  offer: App.Offer
  rooms: Array<App.Occupants>
  checkIn?: string
  packageOptions: Array<App.OfferPackageOptionView>
  roomOption?: OfferPageRoomOption
  showFlightInformation?: boolean
  lowestAvailableStayPayTier?: App.StayPayTier | null
}

function AlwaysOnRoomOptionsMobile(props: Props) {
  const {
    offer,
    packageOptions,
    showFlightInformation,
    roomOption,
    checkIn,
    rooms,
    lowestAvailableStayPayTier,
  } = props

  const intl = useIntl()
  const supportContact = useContext(ContactContext)
  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions)
  }, [packageOptions])

  const packageDuration = packageOptionsByRoom?.[0]?.packageViews?.[0]?.package?.duration
  const checkOut = useMemo(() => checkIn ? moment(checkIn).add(packageDuration, 'days').format(ISO_DATE_FORMAT) : undefined, [checkIn, packageDuration])

  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn,
    checkOut,
    rooms: rooms ?? initialOfferPageRooms,
  })

  const hasRateError = !!offerAvailableRates?.error

  return (<VerticalSpacer gap={16}>
    <LayoutContainer>
      <VerticalSpacer gap={16}>
        <Heading variant="heading4">{intl.formatMessage({
          id: '9OQ1zlx4cBe8cEMBz8yn',
          defaultMessage: 'Room Options',
          description: 'Heading for room options on always on room options mobile',
        })}
        </Heading>
        <HotelFlightsToggleButton />
        {hasRateError && <MessageBanner
          kind="warning"
          tabletAlign="center"
          heading={intl.formatMessage({
            id: 'BnvLw0OFjcVH9KIHccT2',
            defaultMessage: 'The third party booking system for {propertyName} is currently down',
            description: 'Error message heading when property booking system is down',
          }, { propertyName: offer.property?.name })}
        >
          {intl.formatMessage({
            id: '6Ik4jaitlAq16MsoHuLG',
            defaultMessage: 'Please call our VIP team on {phoneNumber} who are ready to provide assistance to complete your booking',
            description: 'Message asking user to call VIP support when the booking system is down',
          }, { phoneNumber: supportContact.local.humanReadable })}
        </MessageBanner>}
      </VerticalSpacer>

    </LayoutContainer>
    {!hasRateError && (
      <VerticalSpacer gap={32}>
        {!checkIn && lowestAvailableStayPayTier && <MessageBanner
          kind="promotional-primary"
          icon={<LineInfoCircleIcon />}
        >
          <FormattedMessage
            id="CJatyN2_OJwCBg-Foiap"
            defaultMessage="<b>Special offer:</b> Enjoy free nights for stays longer than {stayNights, plural, one {# day} other {# days}}. Select dates to check eligibility."
            description="Promotional banner about free nights for longer stays with date selection prompt"
            values={{
              b: (chunks) => <b>{chunks}</b>,
              stayNights: lowestAvailableStayPayTier.stayNights,
            }}
          />
        </MessageBanner>}
        <Pane background="neutral-six">
          <VerticalSpacer gap={8}>
            {packageOptionsByRoom.map((item) => {
              if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
                return null
              }
              return (<AlwaysOnRoomOptionMobile
                key={item.roomId}
                offer={offer}
                packageViews={item.packageViews}
                detailsModalMode="room-and-package"
              />)
            })}
          </VerticalSpacer>
        </Pane>
      </VerticalSpacer>
    )}
    <LayoutContainer>
      {showFlightInformation && <Caption variant="medium" align="center">
        {intl.formatMessage({
          id: 'uDttN3fq8b1Uq9ae1vy0',
          defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brand} full Terms and Conditions apply.',
          description: 'Disclaimer about indicative pricing on always on room options mobile',
        }, { brand: config.brandConfig.name })}
      </Caption>}
    </LayoutContainer>
  </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer,
  checkIn: state.checkInDate,
  rooms: state.rooms,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
  lowestAvailableStayPayTier: getLowestAvailableStayPayTier(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnRoomOptionsMobile, preventRenderWithOpenFilter)
